import React from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Typography } from '@mui/material';
import { useBookingCopyTranslation as useTranslation } from '../i18n/bookingCopy';
import { formatProposalDateTimeWithDuration } from '../utils/tutorBookingChangeProposal';

export interface TutorBookingChangeTimeComparisonProps {
  oldStartIso: string;
  oldEndIso: string;
  newStartIso: string;
  newEndIso: string;
  locale: string;
  timezone?: string | null;
  compact?: boolean;
  /** Drop outer border/background when nested inside a schedule card section. */
  embedded?: boolean;
}

export default function TutorBookingChangeTimeComparison({
  oldStartIso,
  oldEndIso,
  newStartIso,
  newEndIso,
  locale,
  timezone,
  compact = false,
  embedded = false,
}: TutorBookingChangeTimeComparisonProps) {
  const { t } = useTranslation();

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours === 0) return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    }
    return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
  };

  const oldLabel = t('userTutorBookings.changeProposal.currentTime', 'Current lesson time');
  const newLabel = t('userTutorBookings.changeProposal.newTime', 'New time');

  const oldText = formatProposalDateTimeWithDuration(
    oldStartIso,
    oldEndIso,
    locale,
    formatDuration,
    timezone,
  );
  const newText = formatProposalDateTimeWithDuration(
    newStartIso,
    newEndIso,
    locale,
    formatDuration,
    timezone,
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: compact ? 'column' : { xs: 'column', sm: 'row' },
        alignItems: compact ? 'stretch' : { xs: 'stretch', sm: 'center' },
        gap: compact ? 1 : { xs: 1, sm: 1.5 },
        ...(embedded
          ? { p: 0, border: 'none', bgcolor: 'transparent', borderRadius: 0 }
          : {
              p: compact ? 1.5 : 2,
              borderRadius: 1.5,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }),
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {oldLabel}
        </Typography>
        <Typography
          variant={compact ? 'body2' : 'body1'}
          sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
        >
          {oldText}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          px: { xs: 0, sm: 0.5 },
        }}
        aria-hidden
      >
        <ArrowForwardIcon fontSize={compact ? 'small' : 'medium'} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {newLabel}
        </Typography>
        <Typography variant={compact ? 'body2' : 'body1'} fontWeight={600} color="primary.main">
          {newText}
        </Typography>
      </Box>
    </Box>
  );
}
